/**
 * GDPR Data Subject Request form (Art. 15 export / Art. 17 erasure).
 *
 * Mounted on web/privacy.html. Behaviour:
 *
 * - Unauthenticated visitors: see the email fallback only (no form).
 * - Signed-in users: see the full DSR form. Submissions go to the
 *   `privacyDataRequest` Cloud Function via `submitDataRequest()`.
 * - Local pending list reflects offline-buffered requests + the latest
 *   server acknowledgement.
 *
 * State transitions surfaced in UI:
 *   queued_local  → "Sending…" / "Couldn't reach server, will retry"
 *   received      → "Received — completion within 30 days"
 *   completed     → "Completed"
 *   rejected      → "Rejected: <reason>"
 */

import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase.js';
import {
  flushPendingDataRequests,
  listDataRequests,
  submitDataRequest
} from './privacy-compliance.js';

const STATUS_LABEL = {
  queued_local: 'Queued locally — will retry automatically',
  received: 'Received — we will complete within 30 days',
  in_progress: 'In progress',
  completed: 'Completed',
  rejected: 'Rejected'
};

function formatDate(value: unknown): string {
  if (!value) return '';
  try {
    const d = new Date(value as string | number | Date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function el(tag: string, attrs: Record<string, unknown> = {}, children: (Node | string | null | undefined | false)[] = []): HTMLElement {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === false || value === null || value === undefined) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    // eslint-disable-next-line no-undef
    else if (key.startsWith('on') && typeof value === 'function') node.addEventListener(key.slice(2), value as EventListener);
    else node.setAttribute(key, String(value));
  }
  const flatChildren = ([] as (Node | string | null | undefined | false)[]).concat(children);
  for (const child of flatChildren) {
    if (child === null || child === undefined || child === false) continue;
    node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

function renderHistory(container: HTMLElement): void {
  container.replaceChildren();
  const requests = listDataRequests();
  if (requests.length === 0) {
    container.appendChild(
      el('p', { class: 'dsr-history-empty', text: 'No data requests on file for this device.' })
    );
    return;
  }
  const sorted = [...requests].sort(
    (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
  );
  const list = el('ul', { class: 'dsr-history-list' });
  for (const r of sorted) {
    const statusLabel = STATUS_LABEL[r.status] || r.status;
    list.appendChild(
      el('li', { class: `dsr-history-item dsr-history-item--${r.status}` }, [
        el('div', { class: 'dsr-history-row' }, [
          el('span', { class: 'dsr-history-type', text: r.type === 'export' ? 'Export' : 'Delete' }),
          el('span', { class: 'dsr-history-date', text: formatDate(r.requestedAt) })
        ]),
        el('div', { class: 'dsr-history-status', text: statusLabel }),
        r.lastError
          ? el('div', { class: 'dsr-history-error', text: r.lastError })
          : null
      ])
    );
  }
  container.appendChild(list);
}

function setStatus(node: HTMLElement, message: string, tone = 'info'): void {
  node.textContent = message;
  node.dataset.tone = tone;
  node.hidden = !message;
}

function renderForm({ root, user }: { root: HTMLElement; user: { email?: string | null; displayName?: string | null; uid: string } }): void {
  root.replaceChildren();

  const heading = el('h3', { class: 'dsr-form-heading', text: 'Submit a data request' });
  const subhead = el('p', {
    class: 'dsr-form-sub',
    text:
      'Signed in as ' +
      (user.email || user.displayName || user.uid) +
      '. Choose a request type below; we will respond within 30 days (GDPR Art. 12(3)).'
  });

  const fieldType = el('label', { class: 'dsr-field' }, [
    el('span', { class: 'dsr-field-label', text: 'Request type' }),
    (() => {
      const select = el('select', { name: 'type', required: 'required' }, [
        el('option', { value: 'export', text: 'Export my data (Art. 15)' }),
        el('option', { value: 'delete', text: 'Delete my data (Art. 17)' })
      ]);
      return select;
    })()
  ]);

  const fieldEmail = el('label', { class: 'dsr-field' }, [
    el('span', { class: 'dsr-field-label', text: 'Confirm contact email' }),
    el('input', {
      type: 'email',
      name: 'email',
      required: 'required',
      value: user.email || '',
      autocomplete: 'email',
      placeholder: 'you@example.com'
    })
  ]);

  const fieldReason = el('label', { class: 'dsr-field' }, [
    el('span', { class: 'dsr-field-label', text: 'Reason (optional)' }),
    el('textarea', {
      name: 'reason',
      maxlength: '2000',
      rows: '3',
      placeholder: 'Anything you want us to know — not required.'
    })
  ]);

  const status = el('p', { class: 'dsr-form-status', role: 'status', 'aria-live': 'polite', hidden: 'hidden' });
  const submit = el('button', {
    type: 'submit',
    class: 'dsr-submit',
    text: 'Send request'
  });

  const form = el(
    'form',
    {
      class: 'dsr-form',
      novalidate: 'novalidate',
      onsubmit: async (event: SubmitEvent) => {
        event.preventDefault();
        const data = new FormData(form as unknown as HTMLFormElement);
        const type = String(data.get('type') || 'export') as 'export' | 'delete';
        const email = String(data.get('email') || '').trim();
        const reason = String(data.get('reason') || '').trim();
        if (!email) {
          setStatus(status, 'Please confirm your email.', 'error');
          return;
        }
        (submit as HTMLButtonElement).disabled = true;
        setStatus(status, 'Sending request…', 'info');
        try {
          const result = await submitDataRequest(/** @type {'export' | 'delete'} */ (type), {
            email,
            reason
          });
          if (result.status === 'received') {
            setStatus(
              status,
              'Request received. Confirmation will arrive at ' + email + '. Reference: ' + ((result as any).serverId || result.id),
              'success'
            );
            (form as HTMLFormElement).reset();
            const typeSelect = form.querySelector('select[name="type"]');
            const emailInput = form.querySelector('input[name="email"]');
            if (typeSelect instanceof HTMLSelectElement) typeSelect.value = type;
            if (emailInput instanceof HTMLInputElement) emailInput.value = user.email || '';
          } else {
            setStatus(
              status,
              "Saved locally — we'll retry automatically when the network is back. " +
                (result.lastError ? '(' + result.lastError + ')' : ''),
              'warn'
            );
          }
        } catch (err) {
          setStatus(
            status,
            'Could not submit request: ' + (err instanceof Error ? err.message : String(err)),
            'error'
          );
        } finally {
          (submit as HTMLButtonElement).disabled = false;
          renderHistory(historyContainer);
        }
      }
    },
    [fieldType, fieldEmail, fieldReason, submit, status]
  );

  const historyHeading = el('h4', { class: 'dsr-history-heading', text: 'Your recent requests' });
  const historyContainer = el('div', { class: 'dsr-history' });
  renderHistory(historyContainer);

  root.appendChild(heading);
  root.appendChild(subhead);
  root.appendChild(form);
  root.appendChild(historyHeading);
  root.appendChild(historyContainer);

  // Best-effort flush of any queued_local items now that the user is signed in.
  void flushPendingDataRequests().then(() => renderHistory(historyContainer));
}

function renderSignedOut(root: HTMLElement): void {
  root.replaceChildren();
  root.appendChild(
    el('p', {
      class: 'dsr-signed-out',
      text:
        'Sign in to your Hormiga Dormida account to submit an export or deletion request directly from this page. ' +
        "If you don't have an account, email us using the address below and we will action your request manually."
    })
  );
}

/**
 * @param {{ root?: Element | null }} [options]
 */
export function mountDsrForm({ root }: { root?: Element | null } = {}) {
  const target = root || document.getElementById('dsr-form-root');
  if (!target) return () => {};
  const targetEl = target as HTMLElement;
  let unsubscribe = () => {};
  unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
      renderForm({ root: targetEl, user: { email: user.email, displayName: user.displayName, uid: user.uid } });
    } else {
      renderSignedOut(targetEl);
    }
  });
  return () => {
    try {
      unsubscribe();
    } catch {
      /* noop */
    }
  };
}
